import React, { useState, useEffect, useRef } from 'react';
import { Shape } from '../../types/Shape';
import { findNearestConnectionPoint, findShapeAtPoint, getRelativePosition } from '../../utils/connectionUtils';

interface ArrowEndpointHandlesProps {
  arrow: Shape;
  stagePos: { x: number; y: number };
  stageScale: number;
  allShapes: Shape[];
  onStartDrag?: (arrowId: string) => void;
  onStartMove?: (arrowId: string, newX: number, newY: number) => void;
  onStartEnd?: (arrowId: string, finalX: number, finalY: number) => void;
  onEndDrag?: (arrowId: string) => void;
  onEndMove?: (arrowId: string, newX: number, newY: number) => void;
  onEndEnd?: (arrowId: string, finalX: number, finalY: number) => void;
  onSnapUpdate?: (snappedPoint: { x: number; y: number } | null) => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

const ArrowEndpointHandles: React.FC<ArrowEndpointHandlesProps> = ({
  arrow,
  stagePos,
  stageScale,
  allShapes,
  onStartDrag,
  onStartMove,
  onStartEnd,
  onEndDrag,
  onEndMove,
  onEndEnd,
  onSnapUpdate,
  onDraggingChange,
}) => {
  const [isDraggingStart, setIsDraggingStart] = useState(false);
  const [isDraggingEnd, setIsDraggingEnd] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  // useRef로 변경하여 클로저 문제 해결
  const currentPosRef = useRef({ x: 0, y: 0 });

  const handleSize = 12; // 고정 크기

  // 시작점과 끝점 화면 좌표 계산
  const startX = arrow.x * stageScale + stagePos.x;
  const startY = arrow.y * stageScale + stagePos.y;
  const endX = (arrow.x + (arrow.width || 0)) * stageScale + stagePos.x;
  const endY = (arrow.y + (arrow.height || 0)) * stageScale + stagePos.y;

  // 시작점 드래그
  const handleStartMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // 텍스트 선택 방지
    e.stopPropagation();
    setIsDraggingStart(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    if (onDraggingChange) onDraggingChange(true);
    if (onStartDrag) onStartDrag(arrow.id);
  };

  // 끝점 드래그
  const handleEndMouseDown = (e: React.MouseEvent) => {
    e.preventDefault(); // 텍스트 선택 방지
    e.stopPropagation();
    setIsDraggingEnd(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    if (onDraggingChange) onDraggingChange(true);
    if (onEndDrag) onEndDrag(arrow.id);
  };

  useEffect(() => {
    if (!isDraggingStart && !isDraggingEnd) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault(); // 드래그 중 텍스트 선택 방지
      let canvasX = (e.clientX - stagePos.x) / stageScale;
      let canvasY = (e.clientY - stagePos.y) / stageScale;

      // 연결점 스냅
      const nearestPoint = findNearestConnectionPoint(canvasX, canvasY, allShapes, 20);
      if (nearestPoint) {
        canvasX = nearestPoint.x;
        canvasY = nearestPoint.y;
        if (onSnapUpdate) onSnapUpdate(nearestPoint);
      } else {
        if (onSnapUpdate) onSnapUpdate(null);
      }

      // useRef로 현재 위치 저장 (클로저 문제 해결)
      currentPosRef.current = { x: canvasX, y: canvasY };

      if (isDraggingStart && onStartMove) {
        onStartMove(arrow.id, canvasX, canvasY);
      } else if (isDraggingEnd && onEndMove) {
        onEndMove(arrow.id, canvasX, canvasY);
      }
    };

    const handleMouseUp = () => {
      const finalPos = currentPosRef.current;
      if (isDraggingStart) {
        setIsDraggingStart(false);
        if (onDraggingChange) onDraggingChange(false);
        if (onStartEnd) onStartEnd(arrow.id, finalPos.x, finalPos.y);
        if (onSnapUpdate) onSnapUpdate(null);
      } else if (isDraggingEnd) {
        setIsDraggingEnd(false);
        if (onDraggingChange) onDraggingChange(false);
        if (onEndEnd) onEndEnd(arrow.id, finalPos.x, finalPos.y);
        if (onSnapUpdate) onSnapUpdate(null);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingStart, isDraggingEnd, arrow.id, stagePos, stageScale, allShapes, onStartMove, onStartEnd, onEndMove, onEndEnd, onSnapUpdate, onDraggingChange]);

  return (
    <>
      {/* 시작점 핸들 */}
      <div
        style={{
          position: 'absolute',
          left: startX - handleSize / 2,
          top: startY - handleSize / 2,
          width: handleSize,
          height: handleSize,
          backgroundColor: '#3B82F6',
          border: '2px solid white',
          borderRadius: '50%',
          cursor: 'move',
          zIndex: 1001,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
        onMouseDown={handleStartMouseDown}
      />

      {/* 끝점 핸들 */}
      <div
        style={{
          position: 'absolute',
          left: endX - handleSize / 2,
          top: endY - handleSize / 2,
          width: handleSize,
          height: handleSize,
          backgroundColor: '#3B82F6',
          border: '2px solid white',
          borderRadius: '50%',
          cursor: 'move',
          zIndex: 1001,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        }}
        onMouseDown={handleEndMouseDown}
      />
    </>
  );
};

export default ArrowEndpointHandles;

